import * as React from 'react';
import { Modal, Typography, Card, Box, Grid, IconButton, Icon, Button} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CancelIcon from '../../../icons/cancelIcon.svg';
import { Role, VendorV2Dto } from '@inosio/payments-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../shared/sagas/auth/authSlice';
import { InputField } from '../../../components/InputController';
import { E164PhoneRegex, EmailRegex } from '../../../utils/regex';
import { getCreatingVendor, getUpdatingVendor, selectVendorsState } from '../sagas/vendorsSlice';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import { PhoneNumberInput } from '../../../shared/PhoneNumberInput';

const useStyles = makeStyles({
  modalContainer: {
    background: '#FFFFFF',
    boxShadow: 'none',
    borderRadius: '24px',
  },
  titleText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
    textAlign: 'center',
  },
  buttonContainer: {
    position: 'absolute',
    bottom: '20px',
    left: '24px',
    right: '24px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  descriptionText: {
    fontFamily: 'Work Sans !important',
    fontSize: '16px !important',
    lineHeight: '18px !important',
    color: '#000000 !important',
  },
  labelLengthText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '14px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
  },
  globalError: {
    fontFamily: "'Open Sans', sans-serif",
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '18px',
    color: '#DB3939',
    textAlign: 'center',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '100%',
    margin: '0px auto',
  },
});

interface VendorModalProps {
  open: boolean;
  selectedItem?: VendorV2Dto;
  closeCallback: () => void;
  searchDataCallback: () => void;
}

export default function VendorModal(props: VendorModalProps) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    minHeight: '523px',
    pointerEvents: 'auto',
    width: '98%',
    maxWidth: '472px',
    borderRadius: '24px',
    outline: 'none',
  };

  const currentUser = useSelector(selectCurrentUser);
  const { isCreatingVendor, isCreatingVendorSuccess, isCreatingVendorFailed, isUpdatingVendor, isUpdatingVendorSuccess, isUpdatingVendorFailed, errorMessage } = useSelector(selectVendorsState);

  const [name, setName] = React.useState(props.selectedItem?.Name ?? '');
  const [nameError, setNameError] = React.useState('');
  const [contactName, setContactName] = React.useState(props.selectedItem?.ContactName ?? '');
  const [contactNameError, setContactNameError] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState(props.selectedItem?.PhoneNumber ?? '');
  const [phoneNumberError, setPhoneNumberError] = React.useState('');
  const [email, setEmail] = React.useState(props.selectedItem?.Email ?? '');
  const [emailError, setEmailError] = React.useState('');

  const [isSubmitHandled, setIsSubmitHandled] = React.useState(false);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);

  const handleSave = () => {
    setNameError('');
    setContactNameError('');
    setPhoneNumberError('');
    setEmailError('');

    if (!name) setNameError('Name is required.');
    if (!contactName) setContactNameError('Contact name is required.');
    if (!phoneNumber) setPhoneNumberError('Phone number is required.');
    if (phoneNumber && !E164PhoneRegex.test(phoneNumber)) setPhoneNumberError(`Invalid phone number provided.`);
    if (!email) setEmailError('Email is required.');
    if (email && !EmailRegex.test(email)) setEmailError('Email is invalid.');

    if (name && contactName && phoneNumber && E164PhoneRegex.test(phoneNumber) && email && EmailRegex.test(email)) {
      setIsSubmitHandled(true);
      setGlobalError('');

      dispatch({
        type: props.selectedItem ? getUpdatingVendor.type : getCreatingVendor.type,
        payload: {
          Uuid: props.selectedItem?.Uuid,
          Name: name,
          ContactName: contactName,
          PhoneNumber: phoneNumber,
          Email: email,
        },
      });
    }
  };

  const [globalError, setGlobalError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isSubmitHandled) {
      if (isCreatingVendor || isUpdatingVendor) {
        setIsSpinnerModalOpen(true);
      } else if (isCreatingVendorSuccess || isUpdatingVendorSuccess) {
        props.searchDataCallback();
        props.closeCallback();
        setSpinnerStatus('stop');
        setIsSubmitHandled(false);
      } else if (isCreatingVendorFailed || isUpdatingVendorFailed) {
        setIsSpinnerModalOpen(false);
        setIsSubmitHandled(false);
        setGlobalError(errorMessage.message);
      }
    }
  }, [isSubmitHandled, isCreatingVendor, isCreatingVendorSuccess, isCreatingVendorFailed, isUpdatingVendor, isUpdatingVendorSuccess, isUpdatingVendorFailed, errorMessage]);

  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  return (
    <div className={`${classes.modalContainer}`}>
      <Modal open={props.open} onClose={() => props.closeCallback()} aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description">
        <Card sx={style}>
          <Grid container>
            <Grid item xs={2}></Grid>
            <Grid item xs={8}>
              <Typography className={classes.titleText} sx={{ mt: 1 }}>
                {currentUser?.currentProfile.role === Role.MaintenanceStaff ? 'View' : props.selectedItem ? 'Edit' : 'Add'} Vendor
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <IconButton sx={{ ml: '30px' }} onClick={() => props.closeCallback()}>
                <Icon>
                  <img src={CancelIcon} alt="" width={24} height={24} />
                </Icon>
              </IconButton>
            </Grid>
          </Grid>

          {!!globalError?.length && (
            <Box sx={{ mx: 3 }}>
              <Typography className={classes.globalError}>{globalError}</Typography>
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            <div style={{ marginTop: '5px' }}>
              <InputField
                name={'name'}
                label={'Name'}
                value={name}
                isError={false}
                errors={nameError}
                touched={!!nameError}
                onChange={(e) => setName(e.target.value)}
                disabled={currentUser?.currentProfile.role === Role.MaintenanceStaff}
              />
            </div>

            <div style={{ marginTop: '5px' }}>
              <InputField
                name={'ContactName'}
                label={'Vendor Name'}
                value={contactName}
                isError={false}
                errors={contactNameError}
                touched={!!contactNameError}
                onChange={(e) => setContactName(e.target.value)}
                disabled={currentUser?.currentProfile.role === Role.MaintenanceStaff}
              />
            </div>

            <div style={{ marginTop: '5px', marginBottom: '20px' }}>
              <PhoneNumberInput
                value={phoneNumber}
                showErrorMessage={!!phoneNumberError}
                errorMessage={phoneNumberError}
                onChange={(phone) => setPhoneNumber(phone.startsWith('+') ? phone : `+${phone}`)}
                disabled={currentUser?.currentProfile.role === Role.MaintenanceStaff}
              />
            </div>

            <div style={{ marginTop: '5px' }}>
              <InputField
                name={'Email'}
                label={'Email'}
                value={email}
                isError={false}
                errors={emailError}
                touched={!!emailError}
                onChange={(e) => setEmail(e.target.value)}
                disabled={currentUser?.currentProfile.role === Role.MaintenanceStaff}
              />
            </div>

            {currentUser?.currentProfile.role !== Role.MaintenanceStaff && (
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  style={{
                    fontFamily: "'Open Sans', sans-serif",
                    fontStyle: 'normal',
                    fontWeight: 700,
                    width: '87px',
                    marginLeft: '8px',
                    background: '#39A2DB',
                    borderRadius: '24px',
                    textTransform: 'capitalize',
                  }}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </div>
            )}
          </Box>
        </Card>
      </Modal>

      {isSpinnerModalOpen ? (
        <SpinnerModal
          width="472px"
          height="523px"
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => setIsSpinnerModalOpen(false)}
        />
      ) : (
        <></>
      )}
    </div>
  );
}
